/**
 * Web MIDI ↔ ControlRegistry adapter (Shady-style paths under a prefix).
 */

import {
  controlRegistry,
  normalizeControlPath,
  type ControlMessage,
} from "./controlRegistry";

export const MIDI_ALL_PORTS = "__all__";

/** 0 = omni (all channels); 1–16 = accept only that channel. */
export type MidiChannelFilter = number;

export type MidiBridgeConfig = {
  enabled: boolean;
  inputPrefix: string;
  inputDeviceId: string;
  /** Omni (0) or MIDI channel 1–16. */
  channelFilter: MidiChannelFilter;
  outputEnabled: boolean;
  outputPrefix: string;
  outputDeviceId: string;
};

export type MidiBridgeStatus = "idle" | "ready" | "error" | "unsupported";

export type MidiPortOption = {
  id: string;
  name: string;
};

export const DEFAULT_MIDI_BRIDGE_CONFIG: MidiBridgeConfig = {
  enabled: false,
  inputPrefix: "/midi",
  inputDeviceId: MIDI_ALL_PORTS,
  channelFilter: 0,
  outputEnabled: false,
  outputPrefix: "/midi",
  outputDeviceId: MIDI_ALL_PORTS,
};

export function normalizeMidiChannelFilter(value: unknown): MidiChannelFilter {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }
  return Math.min(16, Math.max(1, n));
}

export function normalizeMidiBridgeConfig(
  partial?: Partial<MidiBridgeConfig> | null,
): MidiBridgeConfig {
  const base = { ...DEFAULT_MIDI_BRIDGE_CONFIG, ...(partial ?? {}) };
  return {
    enabled: base.enabled === true,
    inputPrefix: normalizeControlPath(base.inputPrefix) || "/midi",
    inputDeviceId:
      typeof base.inputDeviceId === "string" && base.inputDeviceId
        ? base.inputDeviceId
        : MIDI_ALL_PORTS,
    channelFilter: normalizeMidiChannelFilter(base.channelFilter),
    outputEnabled: base.outputEnabled === true,
    outputPrefix: normalizeControlPath(base.outputPrefix) || "/midi",
    outputDeviceId:
      typeof base.outputDeviceId === "string" && base.outputDeviceId
        ? base.outputDeviceId
        : MIDI_ALL_PORTS,
  };
}

type MidiBridgeHandle = {
  stop: () => void;
  getStatus: () => MidiBridgeStatus;
  getError: () => string | null;
  getInputs: () => MidiPortOption[];
  getOutputs: () => MidiPortOption[];
};

/**
 * Start Web MIDI input (and optional output subscribe). Returns a handle to stop.
 * Status callbacks fire when ports/access change.
 */
export function startMidiBridge(
  config: MidiBridgeConfig,
  options?: {
    onChange?: (state: {
      status: MidiBridgeStatus;
      error: string | null;
      inputs: MidiPortOption[];
      outputs: MidiPortOption[];
    }) => void;
  },
): MidiBridgeHandle {
  const cfg = normalizeMidiBridgeConfig(config);
  const supported =
    typeof navigator !== "undefined"
    && typeof navigator.requestMIDIAccess === "function";

  let status: MidiBridgeStatus = supported ? "idle" : "unsupported";
  let error: string | null = null;
  let inputs: MidiPortOption[] = [];
  let outputs: MidiPortOption[] = [];
  let access: MIDIAccess | null = null;
  let stopped = false;
  const inputCleanups: Array<() => void> = [];
  let outputUnsub: (() => void) | null = null;

  function emit(): void {
    options?.onChange?.({ status, error, inputs, outputs });
  }

  function clearInputHandlers(): void {
    while (inputCleanups.length > 0) {
      inputCleanups.pop()?.();
    }
  }

  function attachInputs(): void {
    clearInputHandlers();
    if (!cfg.enabled || !access) {
      return;
    }
    const ports = selectInputPorts(access, cfg.inputDeviceId);
    const prefix = cfg.inputPrefix;
    for (const input of ports) {
      const handler = (event: MIDIMessageEvent): void => {
        if (!event.data || event.data.length < 1) {
          return;
        }
        const statusByte = event.data[0] ?? 0;
        const data1 = event.data[1] ?? 0;
        const data2 = event.data[2] ?? 0;
        const type = statusByte & 0xf0;
        const channel = (statusByte & 0x0f) + 1;
        if (cfg.channelFilter > 0 && channel !== cfg.channelFilter) {
          return;
        }
        const timestamp = Date.now();
        dispatchMidi(`${prefix}/raw`, [statusByte, data1, data2, channel], timestamp);
        switch (type) {
          case 0x80:
            dispatchMidi(`${prefix}/noteoff/${data1}`, [data2, channel], timestamp);
            break;
          case 0x90:
            if (data2 <= 0) {
              dispatchMidi(`${prefix}/noteoff/${data1}`, [0, channel], timestamp);
            } else {
              dispatchMidi(`${prefix}/noteon/${data1}`, [data2, channel], timestamp);
            }
            break;
          case 0xa0:
            dispatchMidi(`${prefix}/polypressure/${data1}`, [data2, channel], timestamp);
            break;
          case 0xb0:
            dispatchMidi(`${prefix}/cc/${data1}`, [data2, channel], timestamp);
            break;
          case 0xc0:
            dispatchMidi(`${prefix}/program`, [data1, channel], timestamp);
            break;
          case 0xd0:
            dispatchMidi(`${prefix}/aftertouch`, [data1, channel], timestamp);
            break;
          case 0xe0: {
            const raw14 = data1 | (data2 << 7);
            const normalized = raw14 / 8191.5 - 1;
            dispatchMidi(`${prefix}/pitchbend`, [raw14, normalized, channel], timestamp);
            break;
          }
          default:
            break;
        }
      };
      input.addEventListener("midimessage", handler as EventListener);
      inputCleanups.push(() => {
        input.removeEventListener("midimessage", handler as EventListener);
      });
    }
  }

  function attachOutput(): void {
    outputUnsub?.();
    outputUnsub = null;
    if (!cfg.outputEnabled || !access) {
      return;
    }
    const ports = selectOutputPorts(access, cfg.outputDeviceId);
    if (ports.length === 0) {
      return;
    }
    const prefix = cfg.outputPrefix;
    outputUnsub = controlRegistry.subscribe(message => {
      if (message.source === "midi-in") {
        return;
      }
      if (controlRegistry.isBridgeForwardingSuspended()) {
        return;
      }
      const relative = relativePathForPrefix(message.path, prefix);
      if (relative == null) {
        return;
      }
      const bytes = controlMessageToMidi(relative, message);
      if (!bytes) {
        return;
      }
      for (const port of ports) {
        try {
          port.send(bytes);
        } catch {
          // ignore per-port send errors
        }
      }
    });
  }

  function refreshPorts(): void {
    if (!access) {
      return;
    }
    inputs = readPorts(access.inputs.values());
    outputs = readPorts(access.outputs.values());
    attachInputs();
    attachOutput();
    emit();
  }

  if (!supported) {
    emit();
    return {
      stop: () => {
        stopped = true;
      },
      getStatus: () => status,
      getError: () => error,
      getInputs: () => inputs,
      getOutputs: () => outputs,
    };
  }

  if (!cfg.enabled && !cfg.outputEnabled) {
    status = "idle";
    emit();
    return {
      stop: () => {
        stopped = true;
      },
      getStatus: () => status,
      getError: () => error,
      getInputs: () => inputs,
      getOutputs: () => outputs,
    };
  }

  void (async () => {
    try {
      const next = await navigator.requestMIDIAccess({ sysex: false });
      if (stopped) {
        return;
      }
      access = next;
      status = "ready";
      error = null;
      next.onstatechange = () => refreshPorts();
      refreshPorts();
    } catch (err) {
      if (stopped) {
        return;
      }
      status = "error";
      error = err instanceof Error ? err.message : "Failed to access MIDI devices";
      emit();
    }
  })();

  return {
    stop: () => {
      stopped = true;
      clearInputHandlers();
      outputUnsub?.();
      outputUnsub = null;
      if (access) {
        access.onstatechange = null;
        access = null;
      }
      status = "idle";
    },
    getStatus: () => status,
    getError: () => error,
    getInputs: () => inputs,
    getOutputs: () => outputs,
  };
}

function dispatchMidi(path: string, args: number[], timestamp: number): void {
  controlRegistry.dispatch({
    path,
    args,
    timestamp,
    source: "midi-in",
  });
}

function readPorts<T extends MIDIPort>(iter: IterableIterator<T>): MidiPortOption[] {
  return Array.from(iter)
    .map(port => ({
      id: port.id,
      name: port.name?.trim() || port.id || "MIDI Device",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function selectInputPorts(access: MIDIAccess, selectedId: string): MIDIInput[] {
  const ports = Array.from(access.inputs.values()).filter(port => port.state !== "disconnected");
  if (selectedId === MIDI_ALL_PORTS) {
    return ports;
  }
  return ports.filter(port => port.id === selectedId);
}

function selectOutputPorts(access: MIDIAccess, selectedId: string): MIDIOutput[] {
  const ports = Array.from(access.outputs.values()).filter(port => port.state !== "disconnected");
  if (selectedId === MIDI_ALL_PORTS) {
    return ports;
  }
  return ports.filter(port => port.id === selectedId);
}

function relativePathForPrefix(path: string, prefix: string): string | null {
  const normalized = normalizeControlPath(path);
  if (!normalized) {
    return null;
  }
  if (normalized === prefix) {
    return "";
  }
  if (!normalized.startsWith(`${prefix}/`)) {
    return null;
  }
  return normalized.slice(prefix.length + 1);
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(127, Math.round(value)));
}

function clampChannel(value: number): number {
  return Math.max(1, Math.min(16, Math.round(value) || 1));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function controlMessageToMidi(
  relativePath: string,
  message: ControlMessage,
): number[] | null {
  const tokens = relativePath.split("/").filter(token => token.length > 0);
  if (tokens.length === 0) {
    return null;
  }
  const command = tokens[0]?.toLowerCase();
  const args = message.args;

  if (command === "raw") {
    return [clampByte(args[0] ?? 0), clampByte(args[1] ?? 0), clampByte(args[2] ?? 0)];
  }

  if (command === "cc") {
    const withPathChannel = tokens.length >= 3;
    const channel = clampChannel(withPathChannel ? Number(tokens[1]) : (args[1] ?? 1));
    const controller = clampByte(Number(tokens[withPathChannel ? 2 : 1] ?? 0));
    const value = clampByte(args[0] ?? 0);
    return [0xb0 | (channel - 1), controller, value];
  }

  if (command === "noteon" || command === "noteoff" || command === "note") {
    const withPathChannel = tokens.length >= 3;
    const channel = clampChannel(withPathChannel ? Number(tokens[1]) : (args[1] ?? 1));
    const note = clampByte(Number(tokens[withPathChannel ? 2 : 1] ?? 0));
    const velocity = clampByte(args[0] ?? (command === "noteoff" ? 0 : 127));
    const isNoteOn = command === "noteon" || (command === "note" && velocity > 0);
    return [(isNoteOn ? 0x90 : 0x80) | (channel - 1), note, velocity];
  }

  if (command === "pitchbend") {
    const withPathChannel = tokens.length >= 2;
    const channel = clampChannel(withPathChannel ? Number(tokens[1]) : (args[1] ?? 1));
    const raw = Number(args[0] ?? 8192);
    const value14 =
      Math.abs(raw) <= 1
        ? Math.round((clamp(raw, -1, 1) + 1) * 8191.5)
        : Math.round(raw);
    const clamped14 = clamp(value14, 0, 16383);
    return [0xe0 | (channel - 1), clamped14 & 0x7f, (clamped14 >> 7) & 0x7f];
  }

  return null;
}
